import React, { useState, useEffect, useCallback } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { FaLinkedin, FaEnvelope, FaXing } from 'react-icons/fa';
import { SiFreelancer } from "react-icons/si";
import { useLanguage } from '../context/LanguageContext';

type MotionDivProps = HTMLMotionProps<'div'> & {
  className?: string;
};

const MotionDiv = motion.div as React.FC<MotionDivProps>;

// Add this interface for reCAPTCHA response
declare global {
  interface Window {
    grecaptcha: any;
  }
}

const RECAPTCHA_SITE_KEY = '6Ld00wUsAAAAADEjR-oxI9hTCxGcuAh3s5LONBXe';

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{success: boolean; message: string} | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [isFirstInteraction, setIsFirstInteraction] = useState(false);

  // Load reCAPTCHA script on first interaction
  const handleFirstInteraction = useCallback(() => {
    if (!isFirstInteraction) {
      setIsFirstInteraction(true);
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => setRecaptchaReady(true);
      document.body.appendChild(script);
    }
  }, [isFirstInteraction]);

  // Add event listeners for first interaction
  useEffect(() => {
    const form = document.querySelector('form');
    if (form) {
      const handleInteraction = () => {
        handleFirstInteraction();
        // Remove event listeners after first interaction
        form.removeEventListener('focusin', handleInteraction);
        form.removeEventListener('click', handleInteraction);
      };

      form.addEventListener('focusin', handleInteraction);
      form.addEventListener('click', handleInteraction);

      return () => {
        form.removeEventListener('focusin', handleInteraction);
        form.removeEventListener('click', handleInteraction);
      };
    }
  }, [handleFirstInteraction]);

  // Function to execute reCAPTCHA
  const executeRecaptcha = useCallback(async (action: string): Promise<string | null> => {
    if (!window.grecaptcha) {
      console.error('reCAPTCHA not loaded');
      return null;
    }
    
    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed', error);
      return null;
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Load reCAPTCHA if not already loaded
    if (!isFirstInteraction) {
      handleFirstInteraction();
    }
    
    if (!recaptchaReady) {
      setSubmitStatus({
        success: false,
        message: t('contact.form.messages.recaptchaLoading')
      });
      return;
    }
    
    // Client-side validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus({
        success: false,
        message: t('contact.form.messages.requiredFields')
      });
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({
        success: false,
        message: t('contact.form.messages.invalidEmail')
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Execute reCAPTCHA
      const token = await executeRecaptcha('submit');
      
      if (!token) {
        throw new Error('reCAPTCHA execution failed');
      }
      
      // Verify the token and submit form data to the backend
      const response = await verifyRecaptcha(token);
      
      if (!response.success) {
        throw new Error(response.message || 'Form submission failed');
      }
      
      // If we get here, the submission was successful
      setSubmitStatus({
        success: true,
        message: response.message || t('contact.form.messages.success')
      });
      
      // Reset form on success
      setFormData({ name: '', email: '', message: '' });
      
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({
        success: false,
        message: error instanceof Error ? error.message : t('contact.form.messages.error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Function to verify reCAPTCHA token with the PHP backend
  const verifyRecaptcha = async (token: string) => {
    try {
      const response = await fetch('https://matthiasbauer.dev/backend/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          formData: formData
        }),
      });
      return await response.json();
    } catch (error) {
      console.error('reCAPTCHA verification error:', error);
      return { 
        success: false,
        message: 'Verbindung zum Server fehlgeschlagen. Bitte versuchen Sie es später erneut.'
      };
    }
  };

  const socialLinks = [
    /*{
      name: 'GitHub',
      url: 'https://github.com/yourusername',
      icon: <FaGithub className="w-6 h-6" />
    },*/
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/matthias-bauer-00881333/',
      urlText: 'https://www.linkedin.com',
      icon: <FaLinkedin className="w-6 h-6" />
    },
    {
      name: 'Xing',
      url: 'https://www.xing.com/profile/Matthias_Bauer131',
      urlText: 'https://www.xing.com',
      icon: <FaXing className="w-6 h-6" />
    },
    {
      name: 'Freelancemap',
      url: 'https://www.freelancermap.de/profil/softwareentwickler-im-bereich-web-development',
      urlText: 'https://www.freelancermap.de',
      icon: <SiFreelancer className="w-6 h-6" />
    },
    /*{
      name: 'Twitter',
      url: 'https://twitter.com/yourusername',
      icon: <FaTwitter className="w-6 h-6" />
    },*/
    {
      name: 'Email',
      url: 'mailto:kontakt@matthiasbauer.dev',
      urlText: 'kontakt@matthiasbauer.dev',
      icon: <FaEnvelope className="w-6 h-6" />
    }
  ];

  return (
    <div className="py-12" id="contact">
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <h1 className="text-4xl font-bold mb-4 text-center text-indigo-600 dark:text-indigo-400">
          {t('contact.title')}
        </h1>
        <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12">
          {t('contact.subtitle')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-white">
              {t('contact.workTogether')}
            </h2>
            <p className="text-lg text-left text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              {t('contact.description')}
            </p>
            <div className="mt-12">
              <p className="text-left text-gray-600 dark:text-gray-300 mb-4">
                {t('contact.social')}
              </p>
              <div className="space-y-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    <span className="mr-3">{social.icon}</span>
                    <span>{social.urlText}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            {submitStatus ? (
              <div className={`p-4 rounded-lg ${submitStatus.success ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'}`}>
                {submitStatus.message}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('contact.form.placeholders.name')}
                    onFocus={handleFirstInteraction}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('contact.form.placeholders.email')}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('contact.form.placeholders.message')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                </button>
                <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                  {t('contact.form.recaptchaNotice')}{' '}
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    {t('contact.form.privacyPolicy')}
                  </a>{' '}
                  {t('contact.form.and')}{' '}
                  <a 
                    href="https://policies.google.com/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    {t('contact.form.termsOfService')}
                  </a>
                  {t('contact.form.ofGoogle')}
                </p>
              </form>
            )}
          </div>
        </div>
      </MotionDiv>
    </div>
  );
};

export default Contact;
